import { z } from 'zod';

/**
 * POST /api/chat/send request schema
 * Sends a message in a chat or creates a new chat
 */
export const SendMessageRequestSchema = z.object({
  /** The message text to send */
  message: z.string().min(1, 'Message is required'),
  /** The listing ID associated with the chat */
  listingId: z.number(),
  /** Optional chat ID (if sending to existing chat) */
  chatId: z.number().nullish(),
  /**
   * A reCAPTCHA token minted for action `chat_send`. Optional in the schema so a
   * missing one gets the bot gate's refusal, not a 400 naming the field.
   */
  recaptchaToken: z.string().optional(),
  /** The answer to the checkbox, on the resend after a RECAPTCHA_CHALLENGE reply. */
  challengeToken: z.string().max(4096).optional(),
});

/**
 * POST /api/chat/send success response schema
 */
export const SendMessageSuccessResponseSchema = z.object({
  success: z.literal(true),
  chatId: z.number(),
  messageId: z.number(),
  isNewChat: z.boolean(),
});

/**
 * POST /api/chat/send error response schema
 */
export const SendMessageErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  message: z.string().optional(),
  code: z.string().optional(),
  /** With code RECAPTCHA_CHALLENGE: the checkbox key to show. */
  challengeSiteKey: z.string().optional(),
});

/**
 * POST /api/chat/send combined response schema
 * Uses discriminatedUnion on 'success' field
 */
export const SendMessageResponseSchema = z.discriminatedUnion('success', [
  SendMessageSuccessResponseSchema,
  SendMessageErrorResponseSchema,
]);

export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;
export type SendMessageSuccessResponse = z.infer<typeof SendMessageSuccessResponseSchema>;
export type SendMessageErrorResponse = z.infer<typeof SendMessageErrorResponseSchema>;
export type SendMessageResponse = z.infer<typeof SendMessageResponseSchema>;
